"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const getName = (user) => {
  const first = user?.firstName || "";
  const last = user?.lastName || "";
  return `${first} ${last}`.trim() || user?.fullName || "Unnamed";
};

const getDepartmentIds = (user) => {
  if (Array.isArray(user?.departments) && user.departments.length > 0) {
    return user.departments
      .map((item) => (typeof item === "object" ? item?._id : item))
      .filter(Boolean)
      .map(String);
  }
  if (!user?.department) return [];
  const id = typeof user.department === "object" ? user.department._id || "" : user.department;
  return id ? [String(id)] : [];
};

const getDepartmentName = (user, departmentMap) => {
  const namesFromArray = Array.isArray(user?.departments)
    ? user.departments
        .map((item) => (typeof item === "object" ? item?.name : departmentMap.get(item)))
        .filter(Boolean)
    : [];
  if (namesFromArray.length > 0) return namesFromArray.join(", ");
  if (!user?.department) return "Not assigned";
  if (typeof user.department === "object") return user.department.name || "Unknown";
  return departmentMap.get(user.department) || "Unknown";
};

export function DepartmentUserMapping() {
  const [departments, setDepartments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const [departmentsRes, teachersRes, staffRes] = await Promise.all([
        axiosInstance.get(API_ENDPOINTS.departments, { headers }),
        axiosInstance.get(API_ENDPOINTS.usersTeachers, {
          headers,
          params: { status: "all" },
        }),
        axiosInstance.get(API_ENDPOINTS.usersStaff, {
          headers,
          params: { status: "all" },
        }),
      ]);

      setDepartments(departmentsRes.data?.data?.departments || []);
      setTeachers((teachersRes.data?.data?.teachers || []).filter((u) => u.role === "teacher"));
      setStaff((staffRes.data?.data?.staff || []).filter((u) => u.role === "staff"));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load department mapping data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const departmentMap = useMemo(
    () => new Map(departments.map((d) => [d._id, d.name])),
    [departments]
  );

  const users = useMemo(() => {
    const merged = [
      ...teachers.map((u) => ({ ...u, roleLabel: "Teacher" })),
      ...staff.map((u) => ({ ...u, roleLabel: "Staff" })),
    ];

    return merged.filter((user) => {
      const byRole = selectedRole === "all" || user.role === selectedRole;
      const byQuery =
        query.trim() === "" ||
        getName(user).toLowerCase().includes(query.toLowerCase()) ||
        (user.email || "").toLowerCase().includes(query.toLowerCase());
      return byRole && byQuery;
    });
  }, [teachers, staff, selectedRole, query]);

  const visibleUserIds = users.map((u) => u._id);
  const allVisibleSelected =
    visibleUserIds.length > 0 && visibleUserIds.every((id) => selectedUserIds.includes(id));

  const toggleUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    setSelectedUserIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleUserIds.includes(id));
      }
      const next = new Set([...prev, ...visibleUserIds]);
      return Array.from(next);
    });
  };

  const handleAssign = async () => {
    if (!selectedDepartmentId) {
      setError("Please select a department first");
      return;
    }
    if (selectedUserIds.length === 0) {
      setError("Please select at least one teacher or staff member");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const userMap = new Map(users.map((user) => [user._id, user]));
      await Promise.all(
        selectedUserIds.map((userId) => {
          const currentUser = userMap.get(userId);
          if (currentUser?.role === "teacher") {
            const currentDepartmentIds = getDepartmentIds(currentUser);
            const nextDepartmentIds = Array.from(new Set([...currentDepartmentIds, String(selectedDepartmentId)]));
            return axiosInstance.put(
              API_ENDPOINTS.user(userId),
              { departments: nextDepartmentIds, department: nextDepartmentIds[0] || null },
              { headers }
            );
          }
          return axiosInstance.put(
            API_ENDPOINTS.user(userId),
            { department: selectedDepartmentId },
            { headers }
          );
        })
      );

      const selectedDepartmentName = departmentMap.get(selectedDepartmentId) || "selected department";
      setSuccess(`Mapped ${selectedUserIds.length} user(s) to ${selectedDepartmentName}`);
      setSelectedUserIds([]);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to map selected users");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold">Teacher/Staff Department Mapping</h2>
            <p className="text-xs sm:text-sm text-gray-600">
              Select users and map them to a department.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading || saving}
            className="w-full sm:w-auto"
          >
            Refresh
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <Label htmlFor="department">Department</Label>
            <select
              id="department"
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(e.target.value)}
              disabled={loading || saving}
            >
              <option value="">Select department</option>
              {departments.map((department) => (
                <option key={department._id} value={department._id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="role">User Type</Label>
            <select
              id="role"
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                setSelectedUserIds([]);
              }}
              disabled={loading || saving}
            >
              <option value="all">All</option>
              <option value="teacher">Teacher</option>
              <option value="staff">Staff</option>
            </select>
          </div>

          <div>
            <Label htmlFor="search">Search</Label>
            <Input
              id="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or email"
              disabled={loading || saving}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleAllVisible}
              disabled={loading || saving || visibleUserIds.length === 0}
            />
            Select all visible users ({visibleUserIds.length})
          </label>
          <Button
            onClick={handleAssign}
            disabled={loading || saving || selectedUserIds.length === 0 || !selectedDepartmentId}
            className="w-full sm:w-auto"
          >
            {saving ? "Mapping..." : `Map Selected (${selectedUserIds.length})`}
          </Button>
        </div>

        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">Select</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Current Department</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user._id)}
                        onChange={() => toggleUser(user._id)}
                        disabled={loading || saving}
                      />
                    </TableCell>
                    <TableCell>{getName(user)}</TableCell>
                    <TableCell>{user.roleLabel}</TableCell>
                    <TableCell>{user.email || "-"}</TableCell>
                    <TableCell>{getDepartmentName(user, departmentMap)}</TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      {loading ? "Loading users..." : "No users found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
